import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTheme } from "../hooks/useTheme";
import ProfileModal from "./ProfileModal";
import TimeEntryModal from "./TimeEntryModal";
import BriefcaseLogo from "./BriefcaseLogo";

function SunIcon() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="10" cy="10" r="3.25" />
      <path d="M10 1.75v2.5M10 15.75v2.5M18.25 10h-2.5M4.25 10h-2.5M15.83 4.17l-1.76 1.76M5.93 14.07l-1.76 1.76M15.83 15.83l-1.76-1.76M5.93 5.93L4.17 4.17" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M15.53 12.74A6.75 6.75 0 0 1 7.26 4.47a7.25 7.25 0 1 0 8.27 8.27Z" />
    </svg>
  );
}

// Matches `.profile-menu__dropdown { min-width }` in global.css.
const PROFILE_MENU_WIDTH = 250;

export default function AppShell({ children }: { children: React.ReactNode }) {
  const { user, impersonator, logout, stopImpersonating } = useAuth();
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  const isLight = theme === "light";

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAlignLeft, setMenuAlignLeft] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [timeEntryOpen, setTimeEntryOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const adminRef = useRef<HTMLDivElement | null>(null);

  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const isOwner = user?.role === "owner";
  const canLogTime = user?.role === "lawyer" || user?.role === "assistant";
  const inAdminSection =
    loc.pathname.startsWith("/admin") ||
    loc.pathname.startsWith("/activity") ||
    loc.pathname.startsWith("/credits");

  // Close any open menu on route change so navigating via the dropdown
  // doesn't leave the popover stranded over the new page.
  useEffect(() => {
    setMenuOpen(false);
    setAdminOpen(false);
  }, [loc.pathname]);

  // Close the dropdown on outside click / Escape so it never traps focus.
  useEffect(() => {
    if (!menuOpen && !adminOpen) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (adminRef.current && !adminRef.current.contains(e.target as Node)) {
        setAdminOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setAdminOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, adminOpen]);

  const initials = (user?.display_name ?? "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("") || "?";

  return (
    <div className="shell">
      <header className="shell__top">
        <Link to="/" className="shell__brand" aria-label="Cases home">
          <span className="shell__logo" aria-hidden>
            <BriefcaseLogo size={22} />
          </span>
        </Link>
        <nav className="shell__nav">
          <Link to="/" className={loc.pathname === "/" ? "active" : ""}>Cases</Link>
          {canLogTime && (
            <Link to="/time" className={loc.pathname === "/time" ? "active" : ""}>Time entries</Link>
          )}
          {isAdmin && (
            <div className="shell__admin" ref={adminRef}>
              <button
                type="button"
                className={`shell__admin-trigger${inAdminSection ? " active" : ""}`}
                aria-haspopup="menu"
                aria-expanded={adminOpen}
                onClick={() => setAdminOpen((o) => !o)}
              >
                Admin <span className="shell__admin-caret" aria-hidden>▾</span>
              </button>
              {adminOpen && (
                <div className="shell__admin-menu" role="menu">
                  <Link
                    to="/admin/users"
                    role="menuitem"
                    className={loc.pathname.startsWith("/admin/users") ? "active" : ""}
                  >
                    Users
                  </Link>
                  {isOwner && (
                    <Link
                      to="/admin/organizations"
                      role="menuitem"
                      className={loc.pathname.startsWith("/admin/organizations") ? "active" : ""}
                    >
                      Organizations
                    </Link>
                  )}
                  {isOwner && (
                    <Link
                      to="/admin/models"
                      role="menuitem"
                      className={loc.pathname.startsWith("/admin/models") ? "active" : ""}
                    >
                      Models
                    </Link>
                  )}
                  {isOwner && (
                    <Link
                      to="/admin/ai-usage"
                      role="menuitem"
                      className={loc.pathname.startsWith("/admin/ai-usage") ? "active" : ""}
                    >
                      AI usage
                    </Link>
                  )}
                  {isOwner && (
                    <Link
                      to="/admin/tiers"
                      role="menuitem"
                      className={loc.pathname.startsWith("/admin/tiers") ? "active" : ""}
                    >
                      Pricing tiers
                    </Link>
                  )}
                  {isOwner && (
                    <Link
                      to="/admin/billing"
                      role="menuitem"
                      className={loc.pathname.startsWith("/admin/billing") ? "active" : ""}
                    >
                      Billing &amp; credits
                    </Link>
                  )}
                  {/* Owners get every customer; an admin gets the same report
                      scoped to their own organization. */}
                  <Link
                    to="/admin/usage"
                    role="menuitem"
                    className={loc.pathname.startsWith("/admin/usage") ? "active" : ""}
                  >
                    Credit usage
                  </Link>
                  {isOwner && (
                    <Link
                      to="/admin/invoices"
                      role="menuitem"
                      className={loc.pathname.startsWith("/admin/invoices") ? "active" : ""}
                    >
                      Usage statements
                    </Link>
                  )}
                  {isOwner && (
                    <Link
                      to="/admin/generate-demo"
                      role="menuitem"
                      className={loc.pathname.startsWith("/admin/generate-demo") ? "active" : ""}
                    >
                      Generate Demo
                    </Link>
                  )}
                  {!isOwner && (
                    // An admin's own plan and statement history. Owners get the
                    // cross-tenant console above instead, which already covers
                    // their own organization.
                    <Link
                      to="/credits"
                      role="menuitem"
                      className={loc.pathname.startsWith("/credits") ? "active" : ""}
                    >
                      Plan &amp; usage
                    </Link>
                  )}
                  <Link
                    to="/admin/time"
                    role="menuitem"
                    className={loc.pathname.startsWith("/admin/time") ? "active" : ""}
                  >
                    Time tracking
                  </Link>
                  <Link
                    to="/activity"
                    role="menuitem"
                    className={loc.pathname.startsWith("/activity") ? "active" : ""}
                  >
                    Activity log
                  </Link>
                </div>
              )}
            </div>
          )}
        </nav>
        <div className="shell__user">
          {impersonator && user && (
            <div className="impersonation-pill" role="status">
              <span>
                Logged in as <strong>{user.display_name}</strong>
              </span>
              <button
                type="button"
                className="btn btn--sm"
                onClick={stopImpersonating}
              >
                Return to owner
              </button>
            </div>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={isLight}
            aria-label={isLight ? "Switch to dark mode" : "Switch to light mode"}
            title={isLight ? "Switch to dark mode" : "Switch to light mode"}
            className={`theme-toggle${isLight ? " theme-toggle--on" : ""}`}
            onClick={toggle}
          >
            <span className="theme-toggle__icon theme-toggle__icon--moon" aria-hidden><MoonIcon /></span>
            <span className="theme-toggle__icon theme-toggle__icon--sun" aria-hidden><SunIcon /></span>
            <span className="theme-toggle__thumb" aria-hidden />
          </button>

          {user && (
            <div className="profile-menu" ref={menuRef}>
              <button
                type="button"
                className="profile-menu__trigger"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => {
                  // On phones the header wraps and the avatar lands near the
                  // LEFT edge, so a right-anchored dropdown would open
                  // off-screen. Anchor to whichever side has room.
                  const r = menuRef.current?.getBoundingClientRect();
                  setMenuAlignLeft(!!r && r.right < PROFILE_MENU_WIDTH + 12);
                  setMenuOpen((o) => !o);
                }}
              >
                <span className="profile-menu__avatar" aria-hidden>{initials}</span>
                <span className="profile-menu__name">{user.display_name}</span>
                <span className="profile-menu__caret" aria-hidden>▾</span>
              </button>
              {menuOpen && (
                <div
                  className={`profile-menu__dropdown${menuAlignLeft ? " profile-menu__dropdown--align-left" : ""}`}
                  role="menu"
                >
                  <div className="profile-menu__header">
                    <div className="profile-menu__name profile-menu__name--full">
                      {user.display_name}
                    </div>
                    {user.email && (
                      <div className="profile-menu__email">{user.email}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    className="profile-menu__item"
                    onClick={() => { setMenuOpen(false); setProfileOpen(true); }}
                  >
                    Update profile
                  </button>
                  {canLogTime && (
                    <button
                      type="button"
                      role="menuitem"
                      className="profile-menu__item"
                      onClick={() => { setMenuOpen(false); setTimeEntryOpen(true); }}
                    >
                      Log time & mileage
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    className="profile-menu__item profile-menu__item--danger"
                    onClick={() => { setMenuOpen(false); logout(); }}
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      <main className="shell__main">{children}</main>
      {profileOpen && <ProfileModal onClose={() => setProfileOpen(false)} />}
      {timeEntryOpen && <TimeEntryModal onClose={() => setTimeEntryOpen(false)} />}
    </div>
  );
}
